import _ from 'lodash'
import hoistNonReactStatic from 'hoist-non-react-statics'
import {
    PropsWithChildren,
    ComponentType,
    CSSProperties,
    PureComponent,
} from 'react'
import { LoadingOutlined } from '@ant-design/icons'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import {
    Checkbox as AntCheckbox,
    Input,
    InputNumber,
    Select as AntSelect,
    Radio as AntRadio,
    SelectProps,
} from 'antd'
import { TextAreaProps } from 'antd/lib/input/TextArea'
import { InputNumberProps } from 'antd/lib/input-number'
import { InputProps } from 'antd/lib/input/Input'
import { FormItemProps } from '@ant-design/compatible/lib/form/FormItem'
import { SelectValue } from 'antd/lib/select'
import { WrappedFieldInputProps, WrappedFieldProps } from 'redux-form/lib/Field'
import { RadioGroupProps } from 'antd/lib/radio/interface'

type Meta = Partial<WrappedFieldProps['meta']>

const getValidateStatus = ({ touched, error, warning, valid }: Meta = {}) => {
    if (touched) {
        if (error) {
            return 'error'
        }
        if (warning) {
            return 'warning'
        }
        if (valid) {
            return 'success'
        }
    }
    return ''
}

const getHelpMessage = (meta: Meta = {}) => {
    const { touched, error, warning } = meta

    return touched ? error || warning : ''
}

interface WithFormItemProps
    extends Pick<
        FormItemProps,
        | 'colon'
        | 'extra'
        | 'hasFeedback'
        | 'help'
        | 'label'
        | 'labelCol'
        | 'required'
        | 'wrapperCol'
        | 'validateStatus'
    > {
    wrapperStyle?: CSSProperties
    meta?: Meta
    input?: WrappedFieldProps['input']
}

function withFormItem<T extends WithFormItemProps = WithFormItemProps>(
    WrappedComponent: ComponentType<PropsWithChildren<T>>,
) {
    class Enhanced extends PureComponent<T> {
        static displayName = `WithFormItem(${
            WrappedComponent.displayName || WrappedComponent.name || 'Component'
        })`

        onChange: WrappedFieldInputProps['onChange'] = v => {
            const { input } = this.props

            return input?.onChange(v)
        }

        render() {
            const {
                meta: { touched } = {},
                input: inputProps,
                meta,
                colon,
                extra,
                hasFeedback,
                help,
                label,
                labelCol,
                required,
                wrapperCol,
                wrapperStyle,
                validateStatus,
                ...props
            } = this.props
            const _validateStatus =
                validateStatus != null
                    ? validateStatus
                    : getValidateStatus(meta)

            const _help = help || getHelpMessage(meta)

            return (
                <Form.Item
                    colon={colon}
                    extra={extra}
                    hasFeedback={
                        hasFeedback != null
                            ? hasFeedback
                            : touched && !_.isEmpty(_validateStatus)
                    }
                    label={label}
                    labelCol={labelCol}
                    required={required}
                    wrapperCol={wrapperCol}
                    validateStatus={_validateStatus}
                    help={_help}
                    style={wrapperStyle}
                >
                    <WrappedComponent
                        {...(props as T)}
                        input={inputProps}
                        meta={meta}
                        onChange={this.onChange}
                    />
                </Form.Item>
            )
        }
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - I'm scared to remove it, since I don't understand fully where it can break :)
    // todo: it seems like it's safe to delete, but needs thorough testing that it doesn't break anything
    Enhanced.Option = undefined

    hoistNonReactStatic(Enhanced, WrappedComponent)

    return Enhanced
}

type MyInputProps<T> = WithFormItemProps &
    T & {
        'data-lpignore'?: boolean
    }

class _MyTextArea extends PureComponent<MyInputProps<TextAreaProps>> {
    render() {
        const {
            input: inputProps,
            'data-lpignore': ignoreLastPassIcons = true,
            ...props
        } = this.props

        const finalProps = {
            ...inputProps,
            ...props,
            // By default ignore last pass icons: https://lastpass.com/support.php?cmd=showfaq&id=10512
            'data-lpignore': ignoreLastPassIcons,
        }

        return <Input.TextArea {...finalProps} />
    }
}
export const MyTextArea = withFormItem(_MyTextArea)

class _Checkbox extends PureComponent {
    render() {
        return <AntCheckbox {...this.props} />
    }
}
export const Checkbox = withFormItem(_Checkbox)

class _MyNumberInput extends PureComponent<MyInputProps<InputNumberProps>> {
    render() {
        const {
            input: inputProps,
            'data-lpignore': ignoreLastPassIcons = true,
            ...props
        } = this.props

        const finalProps = {
            ...inputProps,
            ...props,
            // By default ignore last pass icons: https://lastpass.com/support.php?cmd=showfaq&id=10512
            'data-lpignore': ignoreLastPassIcons,
        }

        return <InputNumber type="number" {...finalProps} />
    }
}
export const MyNumberInput = withFormItem(_MyNumberInput)

class _MyInput extends PureComponent<MyInputProps<InputProps>> {
    render() {
        const {
            input: inputProps,
            'data-lpignore': ignoreLastPassIcons = true,
            ...props
        } = this.props

        const finalProps = {
            ...inputProps,
            ...props,
            // By default ignore last pass icons: https://lastpass.com/support.php?cmd=showfaq&id=10512
            'data-lpignore': ignoreLastPassIcons,
        }

        return <Input {...finalProps} />
    }
}
export const MyInput = withFormItem(_MyInput)

interface TagsSelectProps extends SelectProps<SelectValue>, WithFormItemProps {
    separators: SelectProps<SelectValue>['tokenSeparators']
}

class _TagsSelect extends PureComponent<TagsSelectProps> {
    static defaultSeparators = [',']

    render() {
        const {
            input: inputProps,
            separators = _TagsSelect.defaultSeparators,
            ...props
        } = this.props

        return (
            <AntSelect
                {...inputProps}
                {...props}
                mode="tags"
                tokenSeparators={separators}
            />
        )
    }
}
export const TagsSelect = withFormItem(_TagsSelect)

interface MyRadioGroupProps extends RadioGroupProps, WithFormItemProps {}

class _RadioGroup extends PureComponent<MyRadioGroupProps> {
    onChange: RadioGroupProps['onChange'] = e => {
        const { input } = this.props

        return input?.onChange(e)
    }

    render() {
        const { input: inputProps, children, ...props } = this.props

        return (
            <AntRadio.Group {...inputProps} {...props} onChange={this.onChange}>
                {children}
            </AntRadio.Group>
        )
    }
}
export const RadioGroup = withFormItem(_RadioGroup)

interface MyLoadingSelectProps {
    style?: CSSProperties
}

function LoadingSelect(props: MyLoadingSelectProps) {
    return (
        <AntSelect
            disabled
            placeholder={<LoadingOutlined spin />}
            style={props.style}
        >
            <AntSelect.Option key="loading" value="loading">
                <LoadingOutlined spin />
            </AntSelect.Option>
        </AntSelect>
    )
}

interface MySelectProps extends SelectProps<SelectValue>, WithFormItemProps {}

class _Select extends PureComponent<MySelectProps> {
    static Option = AntSelect.Option
    static OptGroup = AntSelect.OptGroup

    onChange: SelectProps<SelectValue>['onChange'] = e => {
        const { input } = this.props
        const value = e === undefined ? '' : e

        return input?.onChange(value)
    }

    render() {
        const { input: inputProps, children, loading, ...props } = this.props

        if (loading) {
            return <LoadingSelect style={props.style} />
        }

        return (
            <AntSelect
                {...inputProps}
                // This is needed so that the placeholder attribute works properly.
                // Antd hides the placeholder if value is "" which is the default on inputProps
                value={inputProps?.value || undefined}
                {...props}
                onChange={this.onChange}
                //eslint-disable-next-line  @typescript-eslint/no-empty-function
                onBlur={() => {}}
            >
                {children}
            </AntSelect>
        )
    }
}
export const Select = withFormItem(_Select)
